// FLUID BG EYE : melts the CenterEye vector into the fluid engine as a
// genuine 4th layer, so it rides the same pipeline as the fields and picks
// up the palette ramp, dither/screen, material, and lens post-effects.
//
// HOW : the engine's fragment shader has a built-in "photo melt" stage
// (the studio's Insert-image feature) that mixes a texture's luminance
// into the field value BEFORE all post-effects — exactly the hook we need.
// The published fluid-bg build hardcodes it off (u_hasTex forced to 0
// every frame) and ignores alpha (a transparent eye raster would stamp
// black over the whole pattern). So this module:
//   1. patches the fragment shader at runtime to weight the melt by the
//      texture's alpha (pattern untouched where the eye is transparent),
//   2. uploads the rasterized eye SVG into the melt texture (unit 0),
//   3. wraps gl.uniform1f to force u_hasTex on and feed our blend /
//      liquify / aspect values each frame.
// Needs a mount from fluid-bg's mountNative() (it exposes .gl / .U);
// the fluidBackground() handle hides them.

import {
  EYE_VIEWBOX, EYE_VIEWBOX_DRIP, DRIP_D, BODY_D, PUPIL_D, IRIS_D,
} from "../components/TheEye/eyePaths";

export const DEFAULT_EYE_SETTINGS = {
  melt: true,
  blend: 85,    // 0-100 -> shader u_mix; below ~60 the eye goes ghostly
  liquify: 20,  // 0-100 -> shader u_liq
  scale: 46,    // eye width as % of the raster
  pixel: 1,     // eye's own surface pixelate; 1 = inherit the global one
  showDrip: false,
};

// RASTER : draw the eye paths onto a transparent square canvas. Static
// fills (the triad colors) — only luminance reaches the shader anyway,
// the palette ramp recolors it. Resolves with the canvas.
export function renderEyeToCanvas({ scale = 46, showDrip = false, size = 1024 } = {}) {
  const viewBox = showDrip ? EYE_VIEWBOX_DRIP : EYE_VIEWBOX;
  const [, , vw, vh] = viewBox.split(' ').map(Number);
  const stroke = 'stroke="#333333" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"';
  // Explicit width/height — Firefox draws nothing for SVG images without
  // an intrinsic size
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${vw * 8}" height="${vh * 8}">`,
    showDrip ? `<path d="${DRIP_D}" fill="#13FFED" stroke-width="2.5" ${stroke} />` : '',
    `<path d="${BODY_D}" fill="#13FFED" stroke-width="2" ${stroke} />`,
    `<path d="${PUPIL_D}" fill="#FF01E9" stroke-width="2" stroke="#333333" stroke-miterlimit="10" />`,
    // iris (center dot) black -> luminance 0 -> melts to the darkest
    // palette stop, so it reads as a solid black center against the body
    `<path d="${IRIS_D}" fill="#000000" stroke="none" />`,
    `</svg>`,
  ].join('');

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      const w = size * (scale / 100);
      const h = w * (vh / vw);
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      resolve(canvas);
    };
    img.onerror = () => reject(new Error('eye svg rasterize failed'));
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  });
}

// SHADER PATCH : make the melt stage alpha-weighted and give the eye its
// own surface pixelate. Targeted string replaces against the known engine
// source; bails out (returns false) untouched if the bundle ever changes
// so nothing half-breaks.
const TS_LINE = 'vec3 ts = texture2D(u_tex, clamp(tuv, 0.0, 1.0)).rgb;';
const TS_LINE_PATCHED =
  'vec4 ts4 = texture2D(u_tex, clamp(tuv, 0.0, 1.0)); vec3 ts = ts4.rgb;';
const MIX_LINE = 'f = mix(f, lum, u_mix);';
const MIX_LINE_PATCHED = 'f = mix(f, lum, u_mix * ts4.a);';
// `fc` arrives globally quantized (screen/pixelate stage) — when u_eyePixel
// is set, re-quantize the eye's sampling from the raw fragment coord at its
// own cell size instead, mirroring the engine's own pixelate math
const DECL_LINE = 'uniform float u_mix;';
const DECL_LINE_PATCHED = 'uniform float u_mix;\nuniform float u_eyePixel;';
const ST_LINE = 'vec2 st = fc / u_res;';
const ST_LINE_PATCHED = [
  'vec2 efc = fc;',
  'if (u_eyePixel > 1.5){ efc = (floor(gl_FragCoord.xy / u_eyePixel) + 0.5) * u_eyePixel; }',
  'vec2 st = efc / u_res;',
].join('\n    ');

function patchShaderAlpha(mount) {
  const gl = mount.gl;
  const prog = gl.getParameter(gl.CURRENT_PROGRAM);
  const shaders = gl.getAttachedShaders(prog) || [];
  const frag = shaders.find(s => gl.getShaderParameter(s, gl.SHADER_TYPE) === gl.FRAGMENT_SHADER);
  if (!frag) return false;

  const src = gl.getShaderSource(frag);
  if (src.indexOf(TS_LINE_PATCHED) >= 0) return true; // already patched
  if (src.indexOf(TS_LINE) < 0 || src.indexOf(MIX_LINE) < 0 ||
      src.indexOf(DECL_LINE) < 0 || src.indexOf(ST_LINE) < 0) return false;

  gl.shaderSource(frag, src
    .replace(DECL_LINE, DECL_LINE_PATCHED)
    .replace(ST_LINE, ST_LINE_PATCHED)
    .replace(TS_LINE, TS_LINE_PATCHED)
    .replace(MIX_LINE, MIX_LINE_PATCHED));
  gl.compileShader(frag);
  if (!gl.getShaderParameter(frag, gl.COMPILE_STATUS)) return false;
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
  gl.useProgram(prog);

  // Relinking invalidated the engine's cached uniform locations and reset
  // all uniform values — refresh the cache and restore the sampler units
  // (everything else is re-set each frame in _render).
  mount.U.u_eyePixel = null; // ours — looked up with the rest below
  Object.keys(mount.U).forEach(k => { mount.U[k] = gl.getUniformLocation(prog, k); });
  gl.uniform1i(mount.U.u_tex, 0);
  gl.uniform1i(mount.U.u_glyph, 1);
  gl.uniform1i(mount.U.u_mask, 2);
  const pos = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(pos);
  gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);
  return true;
}

// TEXTURE : upload the eye raster into the melt texture. The engine left
// its u_tex texture bound on unit 0 after init, so texImage2D lands on it.
function uploadEyeTexture(mount, canvas) {
  const gl = mount.gl;
  gl.activeTexture(gl.TEXTURE0);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
}

// MELT : wire it all up on a live mountNative() mount. Returns a handle
// { setParams, setImage, remove } or null when the shader patch fails.
export function meltEyeIntoMount(mount, eyeCanvas, { blend = 65, liquify = 20, pixel = 1 } = {}) {
  if (!mount || !mount.gl || !patchShaderAlpha(mount)) return null;

  const gl = mount.gl;
  const params = { mix: blend / 100, liq: liquify / 100 };
  let active = true;

  // u_eyePixel is ours alone — the engine's render loop never writes it,
  // so a plain set sticks until the next relink/context loss
  const setEyePixel = (v) => {
    if (mount.U.u_eyePixel) gl.uniform1f(mount.U.u_eyePixel, v || 1);
  };
  setEyePixel(pixel);

  uploadEyeTexture(mount, eyeCanvas);

  // Force the melt uniforms every frame: _render hardcodes u_hasTex to 0
  // and u_liq/u_mix to studio defaults, so intercept those four writes.
  const orig = gl.uniform1f;
  gl.uniform1f = function (loc, v) {
    if (active && loc) {
      if (loc === mount.U.u_hasTex) v = 1;
      else if (loc === mount.U.u_liq) v = params.liq;
      else if (loc === mount.U.u_mix) v = params.mix;
      else if (loc === mount.U.u_texAspect) v = 1;
    }
    return orig.call(this, loc, v);
  };

  // GPU context restore rebuilds the engine's program from the original
  // source — re-apply the patch, texture, and eye pixelate when that happens.
  let lastCanvas = eyeCanvas;
  let lastPixel = pixel;
  const onRestored = () => {
    if (active && patchShaderAlpha(mount)) {
      uploadEyeTexture(mount, lastCanvas);
      setEyePixel(lastPixel);
    }
  };
  mount.canvas.addEventListener('webglcontextrestored', onRestored);

  const repaint = () => mount.set({});
  repaint();

  return {
    setParams({ blend: b, liquify: l, pixel: px }) {
      if (b != null) params.mix = b / 100;
      if (l != null) params.liq = l / 100;
      if (px != null) { lastPixel = px; setEyePixel(px); }
      repaint();
    },
    setImage(canvas) {
      lastCanvas = canvas;
      uploadEyeTexture(mount, canvas);
      repaint();
    },
    remove() {
      active = false; // leaves the patched shader in place — inert at u_hasTex 0
      mount.canvas.removeEventListener('webglcontextrestored', onRestored);
      gl.uniform1f = orig;
      repaint();
    },
  };
}
